class PageTransitions {
  constructor() {
    this.main = document.getElementById('MainContent');
    this.bar = document.getElementById('PageTransitionBar');
    this.glass = document.getElementById('PageTransitionGlass');
    this.announcer = document.getElementById('PageTransitionAnnouncer');
    this.cache = new Map();
    this.exitDuration = 300; // keep in sync with #MainContent.page-transitioning in base.css
    this.enterDuration = 320; // keep in sync with #MainContent.page-entering in base.css // keep in sync with base.css
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.inFlightController = null;

    if (!this.main) return;

    document.addEventListener('click', this.onClick.bind(this));
    window.addEventListener('popstate', this.onPopState.bind(this));

    history.replaceState({ url: window.location.href }, '', window.location.href);
  }

  onClick(event) {
    const link = event.target.closest('a');
    if (!this.shouldIntercept(link, event)) return;

    event.preventDefault();
    this.navigate(link.href);
  }

  shouldIntercept(link, event) {
    if (!link || !link.href) return false;
    if (event.defaultPrevented) return false;
    if (event.button !== 0) return false;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return false;
    if (link.target && link.target !== '_self') return false;
    if (link.hasAttribute('download')) return false;
    if (link.dataset.noTransition !== undefined) return false;

    // Don't hijack anything inside facets/filter forms or other custom AJAX widgets
    if (link.closest('facet-filters-form, .facets, [data-no-transition]')) return false;

    let url;
    try {
      url = new URL(link.href, window.location.href);
    } catch (e) {
      return false;
    }

    if (url.origin !== window.location.origin) return false;
    if (url.hash && url.pathname === window.location.pathname && url.search === window.location.search) return false;

    const skipPatterns = [
      /^\/cart/,
      /^\/account/,
      /^\/checkout/,
      /^\/challenge/,
      /\.(pdf|zip|jpg|jpeg|png|gif|svg|mp4|webm)$/i,
    ];
    if (skipPatterns.some((re) => re.test(url.pathname))) return false;

    return true;
  }

  async navigate(url, { isPopState = false } = {}) {
    if (this.inFlightController) this.inFlightController.abort();
    const controller = new AbortController();
    this.inFlightController = controller;

    this.showBar();
    this.main.classList.add('page-transitioning');

    try {
      const html = await this.fetchPage(url, controller.signal);
      await this.wait(this.reducedMotion.matches ? 0 : this.exitDuration);

      this.renderPage(html, url);

      if (!isPopState) {
        history.pushState({ url }, '', url);
      }

      // Swap straight from "receded" to "entering" (pushed forward), then
      // release on the next paint so the browser animates entering -> settled.
      this.main.classList.remove('page-transitioning');
      this.main.classList.add('page-entering');

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this.main.classList.remove('page-entering');
        });
      });

      window.scrollTo({ top: 0, behavior: 'auto' });
      this.main.focus({ preventScroll: true });
      this.announce(document.title);
      this.dispatchPageView();

      await this.wait(this.reducedMotion.matches ? 0 : this.enterDuration);
    } catch (error) {
      if (error.name === 'AbortError') return;
      console.error('Page transition failed, falling back to full navigation', error);
      window.location.href = url;
    } finally {
      this.hideBar();
      this.inFlightController = null;
    }
  }

  async fetchPage(url, signal) {
    if (this.cache.has(url)) return this.cache.get(url);
    const response = await fetch(url, { signal, headers: { 'X-Requested-With': 'page-transitions' } });
    if (!response.ok) throw new Error(`Request failed: ${response.status}`);
    const html = await response.text();
    this.cache.set(url, html);
    return html;
  }

  renderPage(html, url) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const newMain = doc.getElementById('MainContent');

    if (!newMain) {
      window.location.href = url;
      return;
    }

    HTMLUpdateUtility.setInnerHTML(this.main, newMain.innerHTML);
    this.main.dataset.template = newMain.dataset.template || '';

    document.title = doc.title;

    const canonical = document.querySelector('link[rel="canonical"]');
    const newCanonical = doc.querySelector('link[rel="canonical"]');
    if (canonical && newCanonical) canonical.href = newCanonical.href;

    this.updateActiveNavState(url);
  }

  updateActiveNavState(url) {
    const path = new URL(url, window.location.href).pathname;
    document.querySelectorAll('.header__menu-item[href]').forEach((item) => {
      let itemPath;
      try {
        itemPath = new URL(item.getAttribute('href'), window.location.href).pathname;
      } catch (e) {
        return;
      }
      item.classList.toggle('header__active-menu-item', itemPath === path);
    });
  }

  dispatchPageView() {
    if (!window.StandardEvents) return;
    const template = this.main.dataset.template || '';
    document.dispatchEvent(
      new window.StandardEvents.PageViewEvent({
        page: { template, title: document.title, url: window.location.href },
      })
    );
  }

  announce(title) {
    if (!this.announcer) return;
    this.announcer.textContent = '';
    // Timeout ensures screen readers pick up the change even if the text is identical
    setTimeout(() => {
      this.announcer.textContent = `${title}`;
    }, 100);
  }

  onPopState(event) {
    const url = (event.state && event.state.url) || window.location.href;
    this.navigate(url, { isPopState: true });
  }

  showBar() {
    if (this.bar) this.bar.classList.add('is-visible');
    if (this.glass) this.glass.classList.add('is-active');
  }

  hideBar() {
    if (this.bar) this.bar.classList.remove('is-visible');
    if (this.glass) this.glass.classList.remove('is-active');
  }

  wait(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }
}

document.addEventListener('DOMContentLoaded', () => {
  window.Theme = window.Theme || {};
  window.Theme.pageTransitions = new PageTransitions();
});